import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { adminApi } from '../../services/api';
import SidebarPanel from '../../components/admin/SidebarPanel';
import AgGridTable from '../../components/AgGridTable';

const UsersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsSidebar, setShowDetailsSidebar] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Custom Cell Renderer for Actions column
  const ActionsCellRenderer = (props) => {
    const { data } = props;
    if (!data) return null;

    // Determine the edit URL based on user type
    const getEditUrl = (user) => {
      switch(user.user_type) {
        case 'ankr':
          return `/admin/users/ankr/${user.id}`;
        case 'healthcare':
          return `/admin/users/healthcare/${user.id}`;
        case 'patient':
          return `/admin/users/patients/${user.id}`;
        case 'provider':
          return `/admin/providers/${user.id}`;
        case 'sales':
          return `/admin/sales/users/${user.id}`;
        default:
          return `/admin/users/${user.id}`;
      }
    };

    return (
      <div className="btn-group btn-group-sm" role="group">
        <button
          className="btn btn-outline-primary"
          onClick={() => handleSelectUser(data)}
          title="View Details"
        >
          <i className="fas fa-eye me-1"></i>
          View
        </button>
        <Link
          to={getEditUrl(data)}
          className="btn btn-outline-info"
          title="Edit"
        >
          <i className="fas fa-pencil me-1"></i>
          Edit
        </Link>
        <button
          className="btn btn-outline-danger"
          onClick={() => {
            if (window.confirm('Are you sure you want to delete this user?')) {
              console.log('Delete user:', data.id);
            }
          }}
          title="Delete"
        >
          <i className="fas fa-trash-can me-1"></i>
          Delete
        </button>
      </div>
    );
  };

  // ag-Grid Column Definitions
  const [columnDefs] = useState([
    {
      headerName: 'ID',
      field: 'id',
      width: 80,
      hide: true
    },
    {
      headerName: 'Name',
      valueGetter: (params) => {
        if (params.data) {
          return `${params.data.first_name || ''} ${params.data.last_name || ''}`.trim();
        }
        return '';
      },
      width: 150,
      cellRenderer: (params) => {
        if (params.data) {
          const fullName = `${params.data.first_name || ''} ${params.data.last_name || ''}`.trim();
          return (
            <a
              href="#"
              className="text-body fw-bold"
              onClick={(e) => {
                e.preventDefault();
                handleSelectUser(params.data);
              }}
            >
              {fullName || params.data.username}
            </a>
          );
        }
        return null;
      }
    },
    {
      headerName: 'Email',
      field: 'email',
      width: 200
    },
    {
      headerName: 'User Type',
      field: 'user_type',
      width: 100,
      cellRenderer: (params) => {
        if (params.data) {
          const typeColors = {
            'ankr': 'bg-primary',
            'healthcare': 'bg-info',
            'sales': 'bg-warning',
            'patient': 'bg-secondary',
            'provider': 'bg-success'
          };
          const typeLabels = {
            'ankr': 'Ankr',
            'healthcare': 'Healthcare',
            'sales': 'Sales',
            'patient': 'Patient',
            'provider': 'Provider'
          };
          return (
            <span className={`badge ${typeColors[params.data.user_type] || 'bg-secondary'}`}>
              {typeLabels[params.data.user_type] || params.data.user_type}
            </span>
          );
        }
        return null;
      }
    },
    {
      headerName: 'Role',
      valueGetter: (params) => params.data?.role?.name || '-',
      width: 120
    },
    {
      headerName: 'Health Systems',
      valueGetter: (params) => {
        if (params.data?.health_systems && params.data.health_systems.length > 0) {
          return params.data.health_systems.map(hs => hs.health_system_name || hs.health_system?.name || hs.name || '-').join(', ');
        }
        return '-';
      },
      width: 200
    },
    {
      headerName: 'Status',
      field: 'is_active',
      width: 100,
      cellRenderer: (params) => {
        if (params.data) {
          return (
            <span className={`badge ${params.data.is_active ? 'bg-success' : 'bg-danger'}`}>
              {params.data.is_active ? 'Active' : 'Inactive'}
            </span>
          );
        }
        return null;
      }
    },
    {
      headerName: 'Last Login',
      field: 'last_login',
      width: 140,
      valueFormatter: (params) => {
        if (params.value) {
          return new Date(params.value).toLocaleDateString();
        }
        return 'Never';
      }
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: ActionsCellRenderer,
      minWidth: 220,
      sortable: false,
      filter: false
    }
  ]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Build query params
        const params = {};
        if (userTypeFilter) {
          params.user_type = userTypeFilter;
        }
        if (searchQuery) {
          params.search = searchQuery;
        }

        // Call the API
        const response = await adminApi.getUsers(params);

        if (response.data?.success && response.data?.users) {
          setUsers(response.data.users);
        } else {
          setError('Failed to fetch users');
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err.response?.data?.message || err.message || 'Failed to fetch users');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [userTypeFilter, searchQuery]);

  // Handle create mode - automatically open sidebar when route is /admin/users/create
  useEffect(() => {
    if (location.pathname === '/admin/users/create') {
      setCreateMode(true);
      setEditMode(false);
      setShowDetailsSidebar(true);
    } else if (location.pathname.includes('/admin/users/edit/')) {
      // Edit mode detected
      setEditMode(true);
      setCreateMode(false);
      setShowDetailsSidebar(true);
    }
  }, [location.pathname]);

  // Handle URL parameter - automatically open sidebar if userId is in URL
  useEffect(() => {
    if (userId && users.length > 0 && !selectedUser) {
      const user = users.find(u => u.id === userId);
      if (user) {
        setSelectedUser(user);
        setShowDetailsSidebar(true);
        // Don't call handleSelectUser as it would navigate and cause a loop
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, users]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setShowDetailsSidebar(true);
    setCreateMode(false);
    setEditMode(false);
    // Navigate to URL with user ID
    navigate(`/admin/users/${user.id}`);
  };

  const handleCloseSidebar = () => {
    setShowDetailsSidebar(false);
    setSelectedUser(null);
    setCreateMode(false);
    setEditMode(false);
    // Navigate back to base URL to remove the ID from URL
    navigate('/admin/users');
  };

  const handleEditUser = () => {
    if (selectedUser) {
      // Navigate to the appropriate specialized page based on user type
      switch(selectedUser.user_type) {
        case 'ankr':
        case 'ankr_user':
          navigate(`/admin/users/ankr/${selectedUser.id}`);
          break;
        case 'healthcare':
        case 'healthcare_user':
          navigate(`/admin/users/healthcare/${selectedUser.id}`);
          break;
        case 'patient':
          navigate(`/admin/users/patients/${selectedUser.id}`);
          break;
        case 'provider':
          navigate(`/admin/providers/${selectedUser.id}`);
          break;
        case 'sales':
          navigate(`/admin/sales/users/${selectedUser.id}`);
          break;
        default:
          navigate(`/admin/users/${selectedUser.id}`);
      }
    }
  };

  return (
    <>
      <div className="container-fluid">
        {error && <div className="alert alert-danger">{error}</div>}
        <div className="page-title-box d-flex align-items-center justify-content-between mb-3">
          <div className="col-8">
            <h4 className="text-lg">
              All Users
            </h4>
            <p className="text-muted">Unified view of all user types in the system</p>
          </div>
          <div className="col-4 d-flex justify-content-end">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><a href="/admin">Admin</a></li>
                <li className="breadcrumb-item"><a href="">User Management</a></li>
                <li className="breadcrumb-item active">All Users</li>
              </ol>
            </nav>
          </div>
        </div>
        <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
          <Link to="/admin/users/create" className="btn btn-primary">
            <i className="fas fa-plus me-1"></i> Add User
          </Link>
        </div>
        <div className="card">
          <div className="d-flex justify-content-between mb-3 p-2">
            <div className='d-flex gap-2'>
              <select
                className="form-select form-select-sm"
                value={userTypeFilter}
                onChange={(e) => setUserTypeFilter(e.target.value)}
              >
                <option value="">All User Types</option>
                <option value="ankr">Ankr Users</option>
                <option value="healthcare">Healthcare Users</option>
                <option value="sales">Sales Users</option>
                <option value="patient">Patients</option>
                <option value="provider">Providers</option>
              </select>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by name, email, or username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <vr></vr>
              <small className="text-muted text-nowrap align-self-center">
                Showing {users.length} user{users.length !== 1 ? 's' : ''}
              </small>
            </div>
          </div>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="alert text-secondary text-center m-3">
              <p>No users found.</p>
            </div>
          ) : (
            <AgGridTable
              rowData={users}
              columnDefs={columnDefs}
              defaultColDef={{
                flex: 1,
                minWidth: 100,
                resizable: true,
                sortable: true,
                filter: true,
              }}
              gridOptions={{
                autoSizeStrategy: {
                  type: 'fitGridWidth'
                }
              }}
              getRowId={(params) => `${params.data.id}`}
            />
          )}
        </div>
      </div>

      {/* Sidebar for User Details or Create */}
      <SidebarPanel
        isOpen={showDetailsSidebar}
        onClose={handleCloseSidebar}
        title={createMode ? "Create User" : "User Details"}
      >
        {createMode ? (
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-4">Select User Type</h5>
              <p className="text-muted mb-4">Choose the type of user you want to create:</p>

              <div className="d-grid gap-3">
                <button
                  className="btn btn-outline-primary btn-lg text-start"
                  onClick={() => {
                    handleCloseSidebar();
                    navigate('/admin/users/ankr', { state: { mode: 'create' } });
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm me-3">
                      <span className="avatar-title rounded-circle bg-primary bg-gradient">
                        <i className="fas fa-building"></i>
                      </span>
                    </div>
                    <div>
                      <h6 className="mb-1">Ankr User</h6>
                      <p className="text-muted mb-0 small">Internal Ankr staff and admins</p>
                    </div>
                  </div>
                </button>

                <button
                  className="btn btn-outline-info btn-lg text-start"
                  onClick={() => {
                    handleCloseSidebar();
                    navigate('/admin/users/healthcare', { state: { mode: 'create' } });
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm me-3">
                      <span className="avatar-title rounded-circle bg-info bg-gradient">
                        <i className="fas fa-user-md"></i>
                      </span>
                    </div>
                    <div>
                      <h6 className="mb-1">Healthcare User</h6>
                      <p className="text-muted mb-0 small">Doctors, nurses, and healthcare staff</p>
                    </div>
                  </div>
                </button>

                <button
                  className="btn btn-outline-secondary btn-lg text-start"
                  onClick={() => {
                    handleCloseSidebar();
                    navigate('/admin/users/patients', { state: { mode: 'create' } });
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm me-3">
                      <span className="avatar-title rounded-circle bg-secondary bg-gradient">
                        <i className="fas fa-user"></i>
                      </span>
                    </div>
                    <div>
                      <h6 className="mb-1">Patient</h6>
                      <p className="text-muted mb-0 small">Patients with portal access</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        ) : selectedUser && (
          <>
            <div className="card overflow-hidden">
              <div className="card-body">
                <div className="d-flex card-title justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="text-primary mb-1">
                      {selectedUser.first_name} {selectedUser.last_name}
                    </h5>
                    <p className="text-muted mb-0 small">@{selectedUser.username}</p>
                  </div>
                  {!editMode ? (
                    <button onClick={handleEditUser} className="btn btn-primary btn-sm">
                      <i className="fas fa-pencil me-1"></i> Edit
                    </button>
                  ) : (
                    <button onClick={() => navigate(`/admin/users/${selectedUser.id}`)} className="btn btn-secondary btn-sm">
                      <i className="fas fa-times me-1"></i> Cancel
                    </button>
                  )}
                </div>

                {!editMode ? (
                  // Read-only view
                  <>
                    <div className="row">
                      <div className="col-6">
                        <h5 className="font-size-15">User ID:</h5>
                        <p className="text-muted mb-0">{selectedUser.id}</p>
                      </div>
                      <div className="col-6">
                        <h5 className="font-size-15">Status:</h5>
                        <p className="mb-0">
                          <span className={`badge ${selectedUser.is_active ? 'bg-success' : 'bg-danger'}`}>
                            {selectedUser.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="mt-3">
                      <h5 className="font-size-15">User Type:</h5>
                      <p className="text-muted mb-0 text-capitalize">{selectedUser.user_type}</p>
                    </div>

                    <div className="mt-3">
                      <h5 className="font-size-15">Email:</h5>
                      <p className="text-muted mb-0">{selectedUser.email}</p>
                    </div>

                    {selectedUser.phone && (
                      <div className="mt-3">
                        <h5 className="font-size-15">Phone:</h5>
                        <p className="text-muted mb-0">{selectedUser.phone}</p>
                      </div>
                    )}

                    {selectedUser.role && (
                      <div className="mt-3">
                        <h5 className="font-size-15">Role:</h5>
                        <p className="text-muted mb-0">{selectedUser.role.name}</p>
                      </div>
                    )}

                    <div className="mt-3">
                      <h5 className="font-size-15">Last Login:</h5>
                      <p className="text-muted mb-0">
                        {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'Never'}
                      </p>
                    </div>

                    <div className="mt-3">
                      <h5 className="font-size-15">Created At:</h5>
                      <p className="text-muted mb-0">
                        {new Date(selectedUser.created_at).toLocaleString()}
                      </p>
                    </div>
                  </>
                ) : (
                  // Edit mode - redirect to specialized page
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    <p className="mb-2">Redirecting to the specialized page for editing...</p>
                    <p className="mb-0 small text-muted">
                      This user type (<strong>{selectedUser.user_type}</strong>) can be edited on the dedicated management page.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {selectedUser.health_systems && selectedUser.health_systems.length > 0 && (
              <div className="card mt-3">
                <div className="card-body">
                  <h5 className="card-title text-primary mb-3">Health System Memberships</h5>
                  <div className="table-responsive">
                    <table className="table table-sm table-centered mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Health System</th>
                          <th>Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedUser.health_systems.map((hs, idx) => (
                          <tr key={idx}>
                            <td>{hs.health_system_name || hs.health_system?.name || hs.name || '-'}</td>
                            <td>{hs.role?.name || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </SidebarPanel>
    </>
  );
};

export default UsersPage;